import { useState, useEffect, useRef } from "react";
import { MapPin, ChevronDown, X } from "lucide-react";
import { getUserTown, setUserTown } from "./SmartSearch";

const TOWN_GROUPS = [
  { label: "Golden Area", towns: ["Golden", "Parson", "Spillimacheen", "Brisco"] },
  { label: "Columbia Valley", towns: ["Invermere", "Radium Hot Springs", "Windermere", "Panorama", "Fairmont Hot Springs", "Canal Flats", "Edgewater"] },
  { label: "Kimberley & Cranbrook", towns: ["Kimberley", "Cranbrook", "Wasa", "Skookumchuck", "Ta Ta Creek"] },
  { label: "Elk Valley", towns: ["Fernie", "Sparwood", "Elkford", "Hosmer"] },
  { label: "Creston Area", towns: ["Creston", "Wynndel", "Boswell"] },
];

const TownPicker = () => {
  const [show, setShow] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const existing = getUserTown();
    const wasDismissed = localStorage.getItem("bcnu_town_dismissed");
    if (!existing && !wasDismissed) {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectTown = (town) => {
    setUserTown(town);
    setDropdownOpen(false);
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem("bcnu_town_dismissed", "true");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="absolute top-16 left-0 right-0 bg-[#1A2F23]/85 backdrop-blur-sm border-b border-[#D4A017]/20 py-2.5 px-4 md:px-8 z-40"
      data-testid="town-picker"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-[#D4A017] flex-shrink-0" />
          <span className="text-white/70 text-sm hidden sm:inline">Where are you?</span>
        </div>

        <div className="relative flex-1 max-w-xs" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between gap-2 bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white transition-colors"
            data-testid="town-picker-dropdown-btn"
          >
            <span className="text-white/60 truncate">Pick your town</span>
            <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-[#1A2F23] border border-[#D4A017]/20 rounded-lg shadow-2xl max-h-72 overflow-y-auto z-50" data-testid="town-picker-dropdown">
              {TOWN_GROUPS.map((group) => (
                <div key={group.label}>
                  <div className="px-3 pt-2.5 pb-1">
                    <span className="text-[10px] uppercase tracking-wider text-[#D4A017]/70 font-semibold">{group.label}</span>
                  </div>
                  {group.towns.map((town) => (
                    <button
                      key={town}
                      onClick={() => selectTown(town)}
                      className="w-full text-left px-3 py-1.5 text-sm text-white/80 hover:bg-[#D4A017]/15 hover:text-white transition-colors"
                      data-testid={`town-pick-${town.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {town}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={dismiss}
          className="text-white/40 hover:text-white/70 p-1 flex-shrink-0"
          data-testid="town-picker-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TownPicker;
